import api from './api';
import { Deck, CreateDeckDto, DeckWithCreator, DeckCreator, UserDecksResponse } from '../types';

export const deckService = {
  async getDecks(): Promise<Deck[]> {
    const response = await api.get('/decks');
    return response.data;
  },

  async getDecksWithCreator(): Promise<DeckWithCreator[]> {
    const response = await api.get('/decks/with-creator');
    return response.data;
  },

  async getDeck(id: string): Promise<Deck> {
    const response = await api.get(`/decks/${id}`);
    return response.data;
  },

  async getDeckWithCreator(id: string): Promise<DeckWithCreator> {
    const response = await api.get(`/decks/${id}/with-creator`);
    return response.data;
  },

  async getDeckCreator(id: string): Promise<DeckCreator> {
    const response = await api.get(`/decks/${id}/creator`);
    return response.data;
  },

  async createDeck(deckData: CreateDeckDto): Promise<Deck> {
    const response = await api.post('/decks', deckData);
    return response.data;
  },

  async requestAIDeck(aiRequest: {
    topic: string;
    description: string;
    cardCount: number;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    language: 'portuguese' | 'english' | 'spanish';
    additionalInfo?: string;
  }): Promise<{ success: boolean; message: string; deckId?: string }> {
    const response = await api.post('/decks/ai-request', aiRequest);
    return response.data;
  },

  async createDeckFromFile(formData: FormData): Promise<{ success: boolean; message: string; deckId: string }> {
    const response = await api.post('/decks/ai-from-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateDeck(id: string, deckData: Partial<CreateDeckDto>): Promise<Deck> {
    const response = await api.patch(`/decks/${id}`, deckData);
    return response.data;
  },

  async generateSlug(id: string): Promise<{ slug: string }> {
    const response = await api.post(`/decks/${id}/generate-slug`);
    return response.data;
  },

  async deleteDeck(id: string): Promise<void> {
    await api.delete(`/decks/${id}`);
  },

  async getDeckStats(): Promise<{ totalDecks: number; totalCards: number }> {
    const response = await api.get('/decks/stats');
    return response.data;
  },

  // Admin methods
  async getUserDecks(userId: string): Promise<UserDecksResponse> {
    const response = await api.get(`/admin/users/${userId}/decks`);
    return response.data;
  },

  async getAllDecks(search?: string, page?: number, limit?: number): Promise<{ decks: DeckWithCreator[], pagination: { page: number, limit: number, totalCount: number, totalPages: number, hasNext: boolean, hasPrev: boolean } }> {
    const params: any = {};
    if (search) params.search = search;
    if (page) params.page = page;
    if (limit) params.limit = limit;
    const response = await api.get('/admin/decks', { params });
    return response.data;
  },

  async getDeckCreatorAdmin(deckId: string): Promise<DeckCreator> {
    const response = await api.get(`/admin/decks/${deckId}/creator`);
    return response.data;
  },

  // Get admin shared decks
  async getAdminSharedDecks(): Promise<DeckWithCreator[]> {
    const response = await api.get('/decks/shared/admin-decks');
    return response.data;
  },

  // Copy a shared deck to user's collection
  async copyDeck(id: string): Promise<Deck> {
    const response = await api.post(`/decks/${id}/copy`);
    return response.data;
  },

  // Reset deck progress (all card progress back to initial state)
  async resetDeckProgress(id: string): Promise<{ modifiedCount: number }> {
    const response = await api.post(`/decks/${id}/reset-progress`);
    return response.data;
  },

  // Admin: Copy a user deck to admin collection
  async copyDeckToAdmin(deckId: string): Promise<Deck> {
    const response = await api.post(`/admin/decks/${deckId}/copy-to-admin`);
    return response.data;
  },

  // Admin: Copy a deck to a specific user
  async copyDeckToUser(deckId: string, userId: string): Promise<Deck> {
    const response = await api.post(`/admin/decks/${deckId}/copy-to-user`, { userId });
    return response.data;
  },
};
